import { useCallback, useEffect, useState } from "react";
import { WorkspaceOverlay } from "@/components/ui/WorkspaceOverlay";
import { IncidentLifecycleBar } from "@/components/dashboard/IncidentLifecycleBar";
import { IncidentWorkspaceHeader } from "@/components/dashboard/IncidentWorkspaceHeader";
import { IncidentEvidencePanel } from "@/components/dashboard/IncidentEvidencePanel";
import { IncidentIntelligencePanel } from "@/components/dashboard/IncidentIntelligencePanel";
import { PolicyAttributionPanel } from "@/components/dashboard/PolicyAttributionPanel";
import { IncidentExecutionProgress } from "@/components/dashboard/IncidentExecutionProgress";
import { IncidentRecommendedActionPanel } from "@/components/dashboard/IncidentRecommendedActionPanel";
import { IncidentResolutionPanel } from "@/components/dashboard/IncidentResolutionPanel";
import { useIncidentExecution } from "@/hooks/useIncidentExecution";
import {
  deriveInitialLifecyclePhase,
  type IncidentLifecyclePhase,
} from "@/lib/incidentLifecycle";
import { buildResolutionSummary, type ResolutionSummary } from "@/lib/resolutionSummary";
import { fetchTenantLogs, fetchVerificationForIncident, type VerificationHistoryItemApi } from "@/lib/api";
import type { IncidentActionResult, IncidentDetail } from "@/types";
import { assembleEvidenceCenter } from "@aisecure/status";
import { EvidenceCenterPanel, VerificationHistoryPanel } from "@aisecure/status/ui";

interface IncidentWorkspaceProps {
  incidentId: string | null;
  onClose: () => void;
  onLoad: (incidentId: string) => Promise<IncidentDetail>;
  onAction: (incidentId: string, actionId: string) => Promise<IncidentActionResult>;
  onBackgroundRefresh?: () => Promise<void>;
}

export function IncidentWorkspace({
  incidentId,
  onClose,
  onLoad,
  onAction,
  onBackgroundRefresh,
}: IncidentWorkspaceProps) {
  const [detail, setDetail] = useState<IncidentDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lifecyclePhase, setLifecyclePhase] = useState<IncidentLifecyclePhase>("new");
  const [selectedActionId, setSelectedActionId] = useState<string | null>(null);
  const [resolution, setResolution] = useState<ResolutionSummary | null>(null);
  const [showEvidenceCenter, setShowEvidenceCenter] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logLines, setLogLines] = useState<string[] | null>(null);
  const [logsFetchedAt, setLogsFetchedAt] = useState<string | null>(null);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [logsMessage, setLogsMessage] = useState<string | null>(null);
  const [logsAvailable, setLogsAvailable] = useState<boolean | null>(null);
  const [verificationHistory, setVerificationHistory] = useState<VerificationHistoryItemApi[]>([]);
  const [verificationHistoryLoading, setVerificationHistoryLoading] = useState(false);
  const [verificationHistoryError, setVerificationHistoryError] = useState<string | null>(null);

  const loadVerificationHistory = useCallback(async (id: string) => {
    setVerificationHistoryLoading(true);
    setVerificationHistoryError(null);
    try {
      const response = await fetchVerificationForIncident(id);
      setVerificationHistory(response.results ?? []);
    } catch (err) {
      setVerificationHistory([]);
      setVerificationHistoryError(err instanceof Error ? err.message : "Failed to load verification history");
    } finally {
      setVerificationHistoryLoading(false);
    }
  }, []);

  const reloadDetail = useCallback(async () => {
    if (!incidentId) return { resolved: false };
    const next = await onLoad(incidentId);
    setDetail(next);
    return { resolved: next.resolved };
  }, [incidentId, onLoad]);

  const load = useCallback(async () => {
    if (!incidentId) {
      setDetail(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const next = await onLoad(incidentId);
      setDetail(next);
      setLifecyclePhase(deriveInitialLifecyclePhase(next));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load incident");
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [incidentId, onLoad]);

  const execution = useIncidentExecution({
    onAction,
    onReload: reloadDetail,
  });

  useEffect(() => {
    setError(null);
    setSelectedActionId(null);
    setResolution(null);
    setShowEvidenceCenter(false);
    setLogLines(null);
    setLogsError(null);
    setLogsMessage(null);
    setLogsAvailable(null);
    setLogsFetchedAt(null);
    setVerificationHistory([]);
    setVerificationHistoryError(null);
    execution.reset();
    setLifecyclePhase("new");
    void load();
    if (incidentId) {
      void loadVerificationHistory(incidentId);
    }
  }, [incidentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExecute = async (actionId: string) => {
    if (!incidentId || actionId === "view-logs") return;
    setSelectedActionId(actionId);
    setError(null);
    setResolution(null);
    setLifecyclePhase("mitigation_running");
    try {
      const outcome = await execution.execute(incidentId, actionId);
      setLifecyclePhase("verification");
      await onBackgroundRefresh?.();
      if (incidentId) void loadVerificationHistory(incidentId);

      if (outcome.passed) {
        setLifecyclePhase("resolved");
        setResolution(buildResolutionSummary(actionId, outcome.result, outcome.recoveryMs, true));
        // Detail already refreshed inside execution.onReload; re-pull if still open.
        try {
          await reloadDetail();
        } catch {
          /* resolved incidents may leave the attention list */
        }
      } else {
        setLifecyclePhase("under_investigation");
        setResolution(
          buildResolutionSummary(actionId, outcome.result, outcome.recoveryMs, false),
        );
        await reloadDetail();
      }
    } catch (err) {
      setLifecyclePhase("under_investigation");
      setError(err instanceof Error ? err.message : "Action failed");
    }
  };

  const handleViewLogs = async () => {
    if (!detail || detail.tenantSlug === "host") {
      setError("Container logs require a tenant slug");
      return;
    }
    setShowEvidenceCenter(true);
    setLogsLoading(true);
    setLogsError(null);
    try {
      const payload = await fetchTenantLogs(detail.tenantSlug);
      setLogLines(payload.lines);
      setLogsFetchedAt(payload.fetched_at);
      setLogsAvailable(payload.available);
      setLogsMessage(payload.message);
      if (!payload.available) {
        setLogsError(payload.message || "Container logs unavailable");
      }
    } catch (err) {
      setLogsError(err instanceof Error ? err.message : "Failed to fetch logs");
      setLogsAvailable(false);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleCloseIncident = () => {
    setLifecyclePhase("closed");
    void onBackgroundRefresh?.();
    onClose();
  };

  const mitigation = detail?.mitigation;
  const operationalActions = detail?.actions.filter((a) => a.id !== "view-logs") ?? [];
  const logsCommand = detail?.logsCommand ?? null;
  const canViewLogs = Boolean(detail && detail.tenantSlug && detail.tenantSlug !== "host");

  const evidenceState = detail
    ? assembleEvidenceCenter({
        slug: detail.tenantSlug,
        incidentId: detail.id,
        title: detail.title,
        summary: detail.evidenceSummary || detail.explanation,
        incidentEvidence: detail.evidence.map((e) => ({
          kind: e.kind,
          category: e.category,
          label: e.label,
          value: e.value,
          observedAt: e.observedAt,
        })),
        aiAnalysis: detail.aiExplanation || detail.assistantBriefing || null,
        recommendedAction: detail.recommendedAction,
        rootCause: detail.explanation,
        logsCommand,
        logLines,
        logsFetchedAt,
        logsError,
        logsAvailable,
        logsMessage,
      })
    : null;

  const isResolved = lifecyclePhase === "resolved";
  const showActions =
    detail &&
    !isResolved &&
    lifecyclePhase !== "closed" &&
    !execution.running &&
    (lifecyclePhase === "under_investigation" || lifecyclePhase === "new");
  const showClose = isResolved || (detail?.resolved && lifecyclePhase !== "closed");

  return (
    <WorkspaceOverlay
      open={incidentId != null}
      onClose={onClose}
      title={detail?.title ?? "Loading…"}
      subtitle={detail ? `${detail.tenantName} · ${detail.category}` : undefined}
    >
      {loading && !detail ? (
        <p className="iw-section__copy iw-section__copy--muted">Opening incident workspace…</p>
      ) : null}
      {error ? <p className="incident-error">{error}</p> : null}

      {detail ? (
        <>
          <IncidentWorkspaceHeader detail={detail} lifecyclePhase={lifecyclePhase} />
          <IncidentLifecycleBar phase={lifecyclePhase} />

          <section className="iw-section iw-assistant">
            <h3 className="iw-section__title iw-section__title--prominent">Operations AI Assistant</h3>
            <p className="iw-assistant__briefing">{detail.assistantBriefing}</p>
          </section>

          {detail.policy ? <PolicyAttributionPanel policy={detail.policy} /> : null}

          <IncidentEvidencePanel
            summary={detail.evidenceSummary}
            evidence={detail.evidence}
            onViewLogs={canViewLogs ? () => void handleViewLogs() : null}
          />

          {showEvidenceCenter && evidenceState ? (
            <section className="iw-section">
              <EvidenceCenterPanel
                state={evidenceState}
                focusLogs
                logsLoading={logsLoading}
                onFetchLogs={() => void handleViewLogs()}
              />
            </section>
          ) : null}

          <section className="iw-section">
            <VerificationHistoryPanel
              title="Verification history for this incident"
              subtitle="GET /api/verification/incidents/{id} — every stored VerificationResult correlated to this incident."
              items={verificationHistory}
              loading={verificationHistoryLoading}
              error={verificationHistoryError}
              emptyMessage="No persisted verification events are correlated to this incident yet."
              onRefresh={incidentId ? () => void loadVerificationHistory(incidentId) : undefined}
            />
          </section>

          <IncidentIntelligencePanel
            timeline={detail.timeline}
            rootCause={detail.rootCause}
            operationalImpact={detail.operationalImpact}
            recommendations={detail.recommendations}
            narrative={detail.narrative}
            relationships={detail.relationships}
            correlatedIncidentIds={detail.correlatedIncidentIds}
            onExecuteAction={(actionId) => void handleExecute(actionId)}
            actionsRunning={execution.running}
          />

          {isResolved && resolution?.verified ? (
            <IncidentResolutionPanel summary={resolution} />
          ) : null}

          {!isResolved && mitigation ? (
            <IncidentRecommendedActionPanel
              mitigation={mitigation}
              confidenceReasons={detail.confidenceReasons}
              fallbackConfidence={detail.aiConfidence}
              executing={execution.running}
              onExecutePrimary={(actionId) => void handleExecute(actionId)}
            />
          ) : null}

          {showActions ? (
            <section className="iw-section iw-actions-section">
              <h3 className="iw-section__title">Actions</h3>
              <div className="iw-actions">
                {operationalActions.map((action) => (
                  <button
                    key={action.id}
                    type="button"
                    className={`btn ${action.primary ? "btn-primary iw-actions__primary" : "btn-ghost iw-actions__secondary"} ${action.destructive ? "btn-danger" : ""}`}
                    disabled={execution.running}
                    onClick={() => void handleExecute(action.id)}
                  >
                    {execution.running && selectedActionId === action.id ? "Running…" : action.label}
                  </button>
                ))}
                {canViewLogs ? (
                  <button
                    type="button"
                    className="btn btn-ghost iw-actions__secondary"
                    onClick={() => void handleViewLogs()}
                  >
                    View Logs
                  </button>
                ) : null}
              </div>
            </section>
          ) : null}

          {execution.running || execution.steps.length > 0 ? (
            <IncidentExecutionProgress
              steps={execution.steps}
              resultMessage={
                isResolved
                  ? "Incident resolved automatically."
                  : lifecyclePhase === "under_investigation" && resolution && !resolution.verified
                    ? "Verification did not pass. Evidence and briefing updated."
                    : null
              }
            />
          ) : null}

          {showClose ? (
            <section className="iw-section iw-close-section">
              <p className="iw-section__copy iw-section__copy--muted">
                Archive this incident when you have finished reviewing the resolution.
              </p>
              <button type="button" className="btn btn-primary iw-close" onClick={handleCloseIncident}>
                Close Incident
              </button>
            </section>
          ) : null}
        </>
      ) : null}
    </WorkspaceOverlay>
  );
}
